import { Card, Col, Form, Radio, Row, Typography } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useGetBanksOptions } from "../../../../../hooks/useGetBanksOptions";
import { TextAreaField } from "../../../../atoms/FormElement";
import { getValidationProps } from "../../../../libs/form";
import { convertToTitleCase, formatCurrency, formatDateByType } from "../../../../libs/utils";
import { ApprovalBar } from "../../../../molecules/ApprovalBar";
import { ButtonGroup } from "../../../../molecules/Button/ButtonGroup";
import { paymentModeOptions } from "../../utili";
import { Invoice } from "../types";
import { ReplacementChequeFields } from "./ReplacementChequeFields";

const { Text } = Typography;

type SelectedReceipt = {
  receiptNo: string | number;
  chequeNo: string | number;
  bankName: string;
  paidDate: string;
  amountToPay: number;
};

type RecallChequeFormProps = {
  selectedReceipt: SelectedReceipt;
  invoice: Invoice;
  recallFee?: number;
  usageType?: string;
  depositTo?: string;
  onCancel?: () => void;
  onSubmit?: (params: {
    recallType: "recallOnly";
    recallReason?: string;
    waiveOff?: boolean;
    amountToPay?: number;
    bankName?: string;
    paidDate?: string;
  }) => Promise<void>;
  onReplace?: () => void;
  onApprove?: () => Promise<void>;
  onReject?: () => Promise<void>;
  isLoading?: boolean;
  isApprovalPending?: boolean;
  isSingleRow?: boolean;
  isReceiptContext?: boolean;
};

type FieldType = {
  recallType: "recallOnly" | "replaceWithNewReceipt";
  recallReason: string;
  waiveFee: boolean;
  paymentMode: { label: string; value: string } | null;
  amountToPay: number | null;
  paymentRef: string;
  bankName: { label: string; value: string } | null;
  paidDate: dayjs.Dayjs | null;
  depositDate: dayjs.Dayjs | null;
  depositTo: { label: string; value: string } | null;
  depositBank: { label: string; value: string } | null;
  chequeNo?: string;
};

type ReceiptRecall = {
  isRequested?: boolean;
  recallType?: FieldType["recallType"];
  reason?: string;
  modeOfPayment?: string;
  replacementChequeNumber?: string;
  bankName?: string;
  depositToBank?: string;
  paidDate?: string;
  depositDate?: string;
  amountToPay?: number;
  depositTo?: string;
  chequeNo?: string;
};

const toSelectOption = (value?: string | null) => {
  if (!value || typeof value !== "string" || !value.trim()) return null;
  const trimmed = value.trim();
  return { label: trimmed, value: trimmed };
};

const toDayjsValue = (value?: string | null) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const toPaymentModeOption = (mode?: string) => {
  if (!mode || typeof mode !== "string") return null;
  const normalized = mode.trim().toLowerCase().replace(/\s+/g, "_");
  const match = paymentModeOptions.find((opt) => opt.value.toLowerCase() === normalized);
  if (match) return match;
  return { label: convertToTitleCase(mode), value: normalized };
};

const buildRecallFormDefaultValues = (params: {
  isReceiptContext?: boolean;
  recall?: ReceiptRecall;
  recallChequeRequest?: Invoice["recallChequeRequest"];
  depositTo?: string;
}): FieldType => {
  const { isReceiptContext, recall, recallChequeRequest } = params;

  if (isReceiptContext) {
    return {
      recallType: recall?.recallType ?? "recallOnly",
      recallReason: recall?.reason ?? "",
      waiveFee: false,
      chequeNo: recall?.replacementChequeNumber ?? "",
      paymentMode: toPaymentModeOption(recall?.modeOfPayment),
      amountToPay: recall?.amountToPay ?? null,
      paymentRef: recall?.replacementChequeNumber ?? "",
      bankName: toSelectOption(recall?.depositToBank),
      paidDate: toDayjsValue(recall?.paidDate),
      depositDate: toDayjsValue(recall?.depositDate),
      depositTo: recall?.depositTo ? { label: recall.depositTo, value: recall.depositTo } : null,
      depositBank: toSelectOption(recall?.depositToBank),
    };
  }

  return {
    recallType: recallChequeRequest?.recallType ?? "recallOnly",
    recallReason: recallChequeRequest?.recallReason ?? "",
    waiveFee: false,
    paymentMode: recallChequeRequest?.modeOfPayment ? toPaymentModeOption(recallChequeRequest.modeOfPayment) : null,
    amountToPay: recallChequeRequest?.amountToPay ?? null,
    chequeNo: recallChequeRequest?.documentNo ?? "",
    paymentRef: recallChequeRequest?.documentNo ?? "",
    bankName: toSelectOption(recallChequeRequest?.bankName),
    paidDate: toDayjsValue(recallChequeRequest?.paidDate),
    depositDate: toDayjsValue(recallChequeRequest?.depositDate),
    depositTo: recallChequeRequest?.depositTo
      ? { label: recallChequeRequest.depositTo, value: recallChequeRequest.depositTo }
      : null,
    depositBank: toSelectOption(recallChequeRequest?.depositToBank),
  };
};

const radioOptions: { value: FieldType["recallType"]; title: string; description: string }[] = [
  {
    value: "recallOnly",
    title: "Recall only",
    description: "Cancel receipt and remove allocated amount from invoice",
  },
  {
    value: "replaceWithNewReceipt",
    title: "Replace with new receipt",
    description: "Cancel receipt and issue a new receipt for a replacement cheque or other instrument",
  },
];

export const RecallChequeForm = (props: RecallChequeFormProps) => {
  const {
    selectedReceipt,
    invoice,
    recallFee = 0,
    usageType,
    depositTo,
    onCancel,
    onSubmit,
    onReplace,
    onApprove,
    onReject,
    isLoading,
    isApprovalPending,
    isSingleRow,
    isReceiptContext,
  } = props;

  const { recallChequeRequest, recall } = invoice as Invoice & { recall?: ReceiptRecall };

  const { banksOptions } = useGetBanksOptions({ usageType: usageType ?? "" });

  const depositToOptions = useMemo(() => {
    if (!depositTo) return [];
    return [{ label: depositTo, value: depositTo }];
  }, [depositTo]);

  const defaultValues = useMemo(
    () =>
      buildRecallFormDefaultValues({
        isReceiptContext,
        recall,
        recallChequeRequest,
        depositTo,
      }),
    [isReceiptContext, recall, recallChequeRequest, depositTo],
  );

  const methods = useForm<FieldType>({ defaultValues });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const recallType = watch("recallType");

  const [approvalDecision, setApprovalDecision] = useState<"approve" | "reject" | null>(null);
  const isApprovalBarVisible = isApprovalPending && approvalDecision !== null;
  const approvalMessage =
    approvalDecision === "reject"
      ? "Please confirm if you would like to proceed with the rejection."
      : "Please confirm if you would like to proceed with the approval.";

  const handleFormSubmit = handleSubmit(async (data) => {
    if (recallType === "replaceWithNewReceipt") {
      onReplace?.();
      return;
    }
    await onSubmit?.({
      recallType: "recallOnly",
      recallReason: data.recallReason,
      waiveOff: data.waiveFee || undefined,
      amountToPay: selectedReceipt.amountToPay,
      bankName: selectedReceipt.bankName,
      paidDate: selectedReceipt.paidDate,
    });
  });

  return (
    <Card>
      <ApprovalBar
        isVisible={isApprovalBarVisible}
        message={approvalMessage}
        containerStyle={{ marginBottom: 24 }}
        primaryButton={{
          label: "Yes",
          loading: isLoading,
          onClick: approvalDecision === "approve" ? onApprove : onReject,
        }}
        secondaryButton={{
          label: "No",
          onClick: () => setApprovalDecision(null),
          disabled: isLoading,
        }}
      />

      <Row gutter={24} style={{ marginBottom: 12 }}>
        <Col span={8}>
          <div style={{ color: "#7b8794", fontSize: 13, marginBottom: 4 }}>Receipt No</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedReceipt.receiptNo}</div>
        </Col>
        <Col span={8}>
          <div style={{ color: "#7b8794", fontSize: 13, marginBottom: 4 }}>Cheque ref</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{selectedReceipt.chequeNo}</div>
        </Col>
        <Col span={8}>
          <div style={{ color: "#7b8794", fontSize: 13, marginBottom: 4 }}>Cheque date</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{formatDateByType(selectedReceipt.paidDate)}</div>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={8}>
          <div style={{ color: "#7b8794", fontSize: 13, marginBottom: 4 }}>Amount</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{formatCurrency(selectedReceipt.amountToPay)}</div>
        </Col>
        <Col span={16}>
          <div style={{ color: "#7b8794", fontSize: 13, marginBottom: 4 }}>Bank</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{convertToTitleCase(selectedReceipt.bankName)}</div>
        </Col>
      </Row>

      <FormProvider {...methods}>
        <Form layout="vertical" onFinish={handleFormSubmit} className="bf-main" style={{ marginTop: 24 }}>
          <Form.Item label="How would you like to proceed?">
            <Controller
              name="recallType"
              control={control}
              render={({ field: { value, onChange } }) => {
                const handleRecallTypeChange = (nextRecallType: FieldType["recallType"]) => {
                  if (isApprovalPending) return;
                  onChange(nextRecallType);
                };

                return (
                  <Radio.Group
                    value={value}
                    onChange={(e) => handleRecallTypeChange(e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <div style={{ display: "flex", flexDirection: "row", gap: 10 }}>
                      {radioOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={() => handleRecallTypeChange(option.value)}
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            border: `1.5px solid ${value === option.value ? "#1a2b5e" : "#d9d9d9"}`,
                            borderRadius: 6,
                            padding: "10px 14px",
                            cursor: isApprovalPending ? "not-allowed" : "pointer",
                            background: value === option.value ? "#f5f7ff" : "#fff",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <Radio value={option.value} disabled={isApprovalPending} style={{ margin: 0 }} />
                            <div>
                              <Text strong style={{ fontSize: 14 }}>
                                {option.title}
                              </Text>
                              <br />
                              <Text type="secondary" style={{ fontSize: 12, fontWeight: "normal" }}>
                                {option.description}
                              </Text>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Radio.Group>
                );
              }}
            />
          </Form.Item>

          {recallType === "recallOnly" && (
            <TextAreaField
              isRequired
              fieldname="recallReason"
              label="Recall reason"
              control={control}
              disabled={isApprovalPending}
              iProps={{ placeholder: "Enter reason for recalling this cheque...", rows: 2 }}
              {...getValidationProps({ fieldName: "recallReason", errors, isRequired: true })}
            />
          )}
          {isApprovalPending && recallType === "replaceWithNewReceipt" && (
            <ReplacementChequeFields banksOptions={banksOptions} depositToOptions={depositToOptions} disabled />
          )}

          {recallFee > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#f5f5f5",
                borderRadius: 6,
                padding: "10px 16px",
                marginBottom: 20,
              }}
            >
              <Text>Recall fee</Text>
              <Text strong>{formatCurrency(recallFee)}</Text>
            </div>
          )}

          <ButtonGroup
            primaryButton={
              isApprovalPending
                ? {
                    label: "Approve",
                    onClick: () => setApprovalDecision("approve"),
                    disabled: isLoading || isApprovalBarVisible,
                  }
                : {
                    label: recallType === "replaceWithNewReceipt" ? "Next" : "Submit for approval",
                    htmlType: "submit",
                    loading: isLoading,
                  }
            }
            secondaryButton={
              isApprovalPending
                ? {
                    label: "Reject",
                    onClick: () => setApprovalDecision("reject"),
                    disabled: isLoading || isApprovalBarVisible,
                  }
                : isSingleRow
                  ? undefined
                  : { label: "Back", onClick: onCancel, disabled: isLoading }
            }
          />
        </Form>
      </FormProvider>
    </Card>
  );
};
